<template>
  <div class="document-item-menu" data-testid="document-item-menu">
    <tags-selector :document="doc" trigger-locator="button">
      <template #trigger>
        <button
          ref="tagsSelectorTrigger"
          class="tags-selector-trigger"
        ></button>
      </template>
    </tags-selector>
    <i-menu
      ref="menuEl"
      :close-on-item-click="false"
      :trigger-align="iMenuTriggerAlignOptions.RIGHT"
      :width="250"
    >
      <template #trigger>
        <i-button
          :disabled="!menuEnabled"
          v-read-only-check="{
            message: 'Options unavailable for read-only users',
          }"
          class="trigger-button"
          data-testid="document-actions-button"
          :type="iButtonTypes.NEUTRAL"
          :button-style="iButtonStyles.GHOST"
          left-icon="medium-more-menu-vertical"
        />
      </template>

      <template #items>
        <!-- Reject -->
        <i-menu-item
          v-if="documentStatusEnabled && !documentHasErrorStates"
          label="Reject"
          :icon-color="palette['light-error-90']"
          icon="small-solid-delete"
          :checked="doc.review_status === documentStatus.REJECTED"
          :disabled="doc.state !== documentStates.PROCESSED"
          data-testid="i-menu-item-document-rejected"
          @item-click="setDocumentStatus(documentStatus.REJECTED)"
        ></i-menu-item>

        <!-- Accept -->
        <i-menu-item
          v-if="documentStatusEnabled && !documentHasErrorStates"
          label="Accept"
          :icon-color="palette['light-success-90']"
          icon="small-solid-check"
          :checked="doc.review_status === documentStatus.ACCEPTED"
          :disabled="doc.state !== documentStates.PROCESSED"
          data-testid="i-menu-item-document-accepted"
          @item-click="setDocumentStatus(documentStatus.ACCEPTED)"
        ></i-menu-item>

        <div
          v-if="documentStatusEnabled && !documentHasErrorStates"
          class="divider"
        ></div>

        <!-- Tags manager -->
        <i-menu-item
          v-if="!documentHasErrorStates"
          label="Manage Tags"
          icon="small-tag"
          :disabled="doc.state !== documentStates.PROCESSED"
          data-testid="i-menu-item-manage-tags"
          @item-click="openTagsManager()"
        ></i-menu-item>

        <!-- Download -->
        <i-menu-item
          label="Download"
          icon="small_download_button_2"
          :disabled="
            doc.state === documentStates.VIRUS_DETECTED ||
            !doc?.urls?.original_file
          "
          data-testid="i-menu-item-download-document"
          @item-click="downloadDocument()"
        ></i-menu-item>

        <div v-if="!documentHasErrorStates" class="divider"></div>

        <!-- delete -->
        <i-menu-item
          v-if="doc.state !== documentStates.ERROR_UPLOADING"
          label="Delete"
          icon="small-delete-bin-1"
          :icon-color="palette['light-error-110']"
          :text-color="palette['light-error-110']"
          data-testid="i-menu-item-delete-document"
          @item-click="tryDeleteDocument()"
        ></i-menu-item>

        <!-- remove -->
        <i-menu-item
          v-if="doc.state === documentStates.ERROR_UPLOADING"
          label="Remove"
          icon="small-delete-bin-1"
          :icon-color="palette['light-error-110']"
          :text-color="palette['light-error-110']"
          data-testid="i-menu-item-remove-upload"
          @item-click="removeUpload()"
        ></i-menu-item>
      </template>
    </i-menu>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex';
import {
  iButton,
  iButtonStyles,
  iButtonTypes,
  iMenu,
  iMenuItem,
  iMenuTriggerAlignOptions,
  showToast,
} from '@inscribe/signal';
import * as documentStates from '@/constants/documentStates';
import * as documentStatus from '@/constants/documentStatus';
import palette from '@inscribe/signal/dist/palette';
import { downloadDocument } from '@/utils/download';
import TagsSelector from '@/V2/components/TagsSelector.vue';

export default {
  components: {
    iButton,
    iMenu,
    iMenuItem,
    TagsSelector,
  },
  props: {
    doc: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      iButtonStyles,
      iButtonTypes,
      iMenuTriggerAlignOptions,
      documentStates,
      documentStatus,
      palette,
      menuEnabled: true,
    };
  },
  computed: {
    ...mapGetters({
      documentStatusEnabled: 'documentStatusEnabled',
    }),

    documentHasErrorStates() {
      return [
        documentStates.ERROR_PROCESSING,
        documentStates.ERROR_UPLOADING,
      ].includes(this.doc.state);
    },
  },
  methods: {
    ...mapMutations(['SET_SELECTED_FILTER', 'SET_FILTER_PAGE']),
    ...mapActions([
      'deleteDocument',
      'getFilteredDocumentsForCustomer',
      'resetFilteredState',
      'trySetDocumentStatus',
      'fetchAndCacheCustomer',
      'fetchAndCacheFraudSummary',
      'fetchAndCacheFirstPageOfSummaryDocumentsForCustomerUUID',
    ]),
    setDocumentStatus(status) {
      this.trySetDocumentStatus({
        document: this.doc,
        data: { review_status: status },
      })
        .then((currDoc) => {
          const textsMap = {};
          textsMap[documentStatus.ACCEPTED] = 'Accepted';
          textsMap[documentStatus.REJECTED] = 'Rejected';
          textsMap[documentStatus.REQUIRES_REVIEW] = 'Requires Review';
          showToast({
            text: `${this.doc?.name} status changed to ${
              textsMap[currDoc.review_status]
            }`,
          });
        })
        .catch((err) => {
          showToast({
            text: err?.response,
            isError: true,
          });
        });
    },
    downloadDocument() {
      this.isDownloading = true;
      downloadDocument(this.doc.urls.original_file, this.doc.name).then(() => {
        this.isDownloading = false;
      });
    },
    tryDeleteDocument() {
      const customerId = this.doc.customer_id;
      const docData = {
        customerId,
        document: this.doc,
      };
      this.menuEnabled = false;
      this.closeMenu();
      this.deleteDocument(docData).then((result) => {
        this.menuEnabled = true;
        if (result) {
          this.$emit('document-deleted', docData);
        }
      });
      // Refetch the associated customer-related data. In particular, the
      // customer-level agent results change when a document is deleted.
      this.fetchAndCacheCustomer(customerId);
      this.fetchAndCacheFraudSummary({ customerId });
      this.fetchAndCacheFirstPageOfSummaryDocumentsForCustomerUUID(customerId);
    },
    removeUpload() {
      const documents = this.$store.state.documents.filter(
        (currDoc) => currDoc.uploadId !== this.doc.uploadId,
      );
      this.$store.commit('setDocuments', documents);
    },
    openTagsManager() {
      this.$refs.tagsSelectorTrigger.click();
      this.closeMenu();
    },
    closeMenu() {
      this.$refs.menuEl?.setMenuOpen(false);
    },
  },
};
</script>

<style lang="scss" scoped>
.document-item-menu {
  position: relative;

  :deep(.tags-selector) {
    height: 0;
    width: 0;

    .tags-selector-trigger {
      padding: 0;
      border: 0;
    }
  }

  .trigger-button {
    :deep(.i-icon) {
      stroke-width: 0 !important;
    }
  }

  .tags-dropdown-container {
    position: absolute !important;
    top: 0;

    .tags-button {
      display: none;
    }
  }
}
</style>
